// Phase 2 of transcript merge v2: owns the workspace-level state that
// holds pending merge conflicts and resolves them via
// `applyConflictResolutions`. The shell mounts the
// `<LeadMergeConflictModal>` based on the `pending` state this hook exposes;
// callers (extraction, re-extract) push conflicts here via `onMergeConflicts`.

import { useCallback, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { Lead } from "../../types/lead";
import {
  applyConflictResolutions,
  type ConflictResolution,
  type MergeLeadResult,
} from "@/lib/lead/extraction/mergeLeadFromTranscript";

export type MergeConflictSource = "extract" | "reextract";

export type PendingMergeConflicts = {
  leadId: string;
  mergeResult: MergeLeadResult;
  source: MergeConflictSource;
  /** Snapshot of the lead at conflict time. Used to apply resolutions even if the active lead has shifted by the time the user clicks Apply. */
  leadSnapshot: Lead;
};

type UseLeadMergeConflictControllerArgs = {
  setLeads: Dispatch<SetStateAction<Lead[]>>;
  setCurrentLead: Dispatch<SetStateAction<Lead | null>>;
  currentLeadRef: React.MutableRefObject<Lead | null>;
  /** Best-effort actor id (used to re-stamp manual overrides on resolution). */
  getActorUserId: () => string | null;
};

export type LeadMergeConflictController = {
  /** Mounted by the shell modal layer. */
  pending: PendingMergeConflicts | null;
  /** Called from the extraction/re-extract paths when conflicts appear. */
  handleMergeConflicts: (args: {
    leadId: string;
    mergeResult: MergeLeadResult;
    source: MergeConflictSource;
  }) => void;
  /** User picked Apply — commit the resolutions. */
  resolve: (resolutions: Record<string, ConflictResolution>) => void;
  /** User cancelled — leave the lead as-is (typed values already preserved). */
  cancel: () => void;
};

export function useLeadMergeConflictController(
  args: UseLeadMergeConflictControllerArgs,
): LeadMergeConflictController {
  const { setLeads, setCurrentLead, currentLeadRef, getActorUserId } = args;
  const [pending, setPending] = useState<PendingMergeConflicts | null>(null);

  const handleMergeConflicts = useCallback(
    (incoming: { leadId: string; mergeResult: MergeLeadResult; source: MergeConflictSource }) => {
      if (incoming.mergeResult.conflicts.length === 0) return;
      const active = currentLeadRef.current;
      // Skip when we can't find the lead — extraction may have fired on a
      // stale lead id (lead switched). Better to silently no-op than to
      // open a modal against missing data.
      if (!active || active.id !== incoming.leadId) return;
      setPending({ ...incoming, leadSnapshot: active });
    },
    [currentLeadRef],
  );

  const resolve = useCallback(
    (resolutions: Record<string, ConflictResolution>) => {
      const current = pending;
      if (!current) return;
      const applied = applyConflictResolutions({
        mergeResult: current.mergeResult,
        resolutions,
        actorUserId: getActorUserId(),
      });
      const nextLead: Lead = {
        ...current.leadSnapshot,
        extracted: applied.nextExtracted,
        user_overrides: applied.nextUserOverrides,
        lastUpdated: Date.now(),
      };

      setLeads((prev) => prev.map((l) => (l.id === current.leadId ? nextLead : l)));
      if (currentLeadRef.current?.id === current.leadId) {
        setCurrentLead(nextLead);
        currentLeadRef.current = nextLead;
      }
      setPending(null);
    },
    [pending, setLeads, setCurrentLead, currentLeadRef, getActorUserId],
  );

  const cancel = useCallback(() => {
    setPending(null);
  }, []);

  return useMemo(
    () => ({ pending, handleMergeConflicts, resolve, cancel }),
    [pending, handleMergeConflicts, resolve, cancel],
  );
}
